import React, { useState, useEffect } from 'react'
import GoogleMapReact from 'google-map-react'
import Supercluster from 'supercluster'
import { useNavigate, useLocation } from 'react-router-dom'
import { getZoom, getCenter } from '../../js/mapHelpers'
import CourseMarker from '../country/CourseMarker'
import ClusterMarker from '../country/ClusterMarker'
import Popup from '../country/Popup'

const Marker = ({ children }) => children

function CountryMap(props) {
  const navigate = useNavigate()
  const location = useLocation()
  const propsZoom = props.zoom || 5.5

  const [points, setPoints] = useState([])
  const [zoom, setZoom] = useState(propsZoom)
  const [bounds, setBounds] = useState(null)
  const [supercluster, setSupercluster] = useState(null)
  const [popup, setPopup] = useState(null)
  const [defaultZoom] = useState(getZoom(location, propsZoom))
  const [defaultCenter] = useState(
    getCenter(location, props.country, props.state)
  )

  const mapRef = React.createRef()

  const createSuperclusterState = (courses) => {
    console.log('createSuperclusterState')
    const coursesWithLocation = courses.filter((course) => course.location)
    const points = coursesWithLocation.map((course) => ({
      type: 'Feature',
      properties: { cluster: false, id: course.id, category: course.name },
      geometry: {
        type: 'Point',
        coordinates: [
          course.location.lng * 0.000001,
          course.location.lat * 0.000001,
        ],
      },
    }))

    const supercluster = new Supercluster({
      radius: 40,
      maxZoom: 20,
    })

    supercluster.load(points)

    return {
      courses,
      points,
      supercluster,
    }
  }

  useEffect(() => {
    const _state = createSuperclusterState(props.courses)
    setPoints(_state.points)
    setSupercluster(_state.supercluster)
  }, [props.courses])

  const onCourseClicked = (cluster) => {
    console.log('Click course', cluster)
    navigate(`/course/${cluster.properties.id}`)
  }

  const onClusterClicked = (cluster) => {
    const children = supercluster.getChildren(cluster.id)
    console.log('Click onClusterClicked', cluster, children)
    setPopup({
      cluster,
      children,
    })
  }

  const renderMarkers = () => {
    // console.log('bounds', bounds, 'zoom', zoom, 'points', points)
    if (!supercluster || !bounds || bounds.length === 0) return null

    try {
      const clusters = supercluster.getClusters(bounds, zoom)
      return clusters.map((cluster) => {
        const [longitude, latitude] = cluster.geometry.coordinates
        const { cluster: isCluster, point_count: pointCount } =
          cluster.properties

        if (isCluster) {
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              lat={latitude}
              lng={longitude}
            >
              <ClusterMarker
                cluster={cluster}
                supercluster={supercluster}
                mapRef={mapRef}
                pointCount={pointCount}
                pointsLength={points.length}
                onClickCluster={(cluster) => onClusterClicked(cluster)}
              />
            </Marker>
          )
        }

        return (
          <Marker
            key={`course-${cluster.properties.id}`}
            lat={latitude}
            lng={longitude}
          >
            <CourseMarker
              cluster={cluster}
              onClick={() => onCourseClicked(cluster)}
            />
          </Marker>
        )
      })
    } catch (e) {
      console.error(e, 'Failed to render markers')
      return null
    }
  }

  return (
    // Important! Always set the container height explicitly
    <div style={{ height: '50vh', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_MAPS_KEY }}
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        center={getCenter(location, props.country, props.state)}
        zoom={getZoom(location, propsZoom)}
        yesIWantToUseGoogleMapApiInternals={true}
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map
        }}
        onChange={({ center, zoom, bounds }) => {
          console.log('onChange', center, zoom, bounds)
          const currentSearch = location.search
          const newSearch = `?zoom=${zoom}&center=${center.lat},${center.lng}`
          if (newSearch !== currentSearch) {
            navigate(location.pathname + newSearch)
          }
          setZoom(zoom)
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat,
          ])
        }}
        options={{
          mapTypeControl: true,
        }}
      >
        {renderMarkers()}
        {popup && (
          <Marker
            key="popup"
            lat={popup.cluster.geometry.coordinates[1]}
            lng={popup.cluster.geometry.coordinates[0]}
          >
            <Popup
              clusters={popup.children}
              onClickCourse={(cluster) => onCourseClicked(cluster)}
            />
          </Marker>
        )}
      </GoogleMapReact>
    </div>
  )
}

export default CountryMap
